/**
 * Subscription Selling Plans Component
 * Handles variant tab selection, selling plan card rendering,
 * per-day price calculation, and form selling_plan input management.
 */

class SubscriptionSellingPlans extends HTMLElement {
  /** @type {number | null} */
  #selectedVariantId = null;

  /** @type {string | null} */
  #selectedSellingPlanId = null;

  /** @type {Array<any> | null} */
  #plansData = null;

  /** @type {Record<string, any> | null} */
  #settings = null;

  /** @type {Array<any> | null} */
  #planSettings = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {'one-time' | 'subscribe'} */
  #purchaseMode = 'subscribe';

  /** @type {Map<number, string>} */
  #selectedPlansByVariant = new Map();

  /** @type {{id: number, variants: Array<any>} | null} */
  #oneTimeProductData = null;

  /** @type {number | null} */
  #selectedOneTimeVariantId = null;

  connectedCallback() {
    this.#loadPlansData();
    this.#initVariantTabs();
    this.#initAccordions();
    this.#listenForVariantUpdates();
    this.#setInitialPurchaseMode();

    // Set up subscribe amount cards (all rendered by Liquid)
    this.#initAmountCards();
    const activeSubscribeTab = this.querySelector('[data-variant-tabs="subscribe"] .ssp-variant-tab--active');
    if (activeSubscribeTab instanceof HTMLElement && activeSubscribeTab.dataset.option1) {
      this.#showSubscribeAmountGroup(activeSubscribeTab.dataset.option1);
    }

    // Render one-time plans for the active one-time tab
    const activeOneTimeTab = this.querySelector('[data-variant-tabs="one-time"] .ssp-variant-tab--active');
    if (activeOneTimeTab instanceof HTMLElement && activeOneTimeTab.dataset.option1) {
      this.#renderOneTimePlansForOption1(activeOneTimeTab.dataset.option1);
    } else if (this.#selectedVariantId) {
      this.#renderOneTimePlansForVariant(this.#selectedVariantId);
    }

    this.#updateFormAndButton();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #loadPlansData() {
    const jsonEl = document.querySelector(
      `script[data-selling-plans-data][data-block-id="${this.dataset.blockId}"]`
    );
    if (!jsonEl) return;
    try {
      const data = JSON.parse(jsonEl.textContent);
      this.#plansData = data.variants || [];
      this.#settings = data.settings || {};
      this.#planSettings = data.planSettings || [];
      this.#oneTimeProductData = data.oneTimeProduct || null;
    } catch (e) {
      console.error('Failed to parse selling plans data:', e);
    }
  }

  #initVariantTabs() {
    const tabGroups = this.querySelectorAll('[data-variant-tabs]');
    if (tabGroups.length === 0) return;

    tabGroups.forEach((group) => {
      const tabs = group.querySelectorAll('[data-variant-tab]');
      tabs.forEach(
        /** @param {HTMLElement} tab */
        (tab) => {
          tab.addEventListener(
            'click',
            () => {
              this.#onVariantTabClick(tab);
            },
            { signal: this.#abortController.signal }
          );
        }
      );
    });
  }

  /**
   * @param {HTMLElement} tab
   */
  #onVariantTabClick(tab) {
    try {
      const variantId = tab.dataset.variantId;
      const tabGroup = tab.dataset.variantTab;
      const option1 = tab.dataset.option1;

      if (!tabGroup) return;

      const numericVariantId = variantId ? Number(variantId) : null;

      // Update active state within this tab group only
      const groupEl = this.querySelector(`[data-variant-tabs="${tabGroup}"]`);
      if (groupEl) {
        groupEl.querySelectorAll('[data-variant-tab]').forEach(
          /** @param {HTMLElement} t */
          (t) => {
            if (t === tab) {
              t.classList.add('ssp-variant-tab--active');
            } else {
              t.classList.remove('ssp-variant-tab--active');
            }
          }
        );
      }

      if (tabGroup === 'subscribe' && option1) {
        this.#showSubscribeAmountGroup(option1);
        this.#updateFormAndButton();
      } else if (tabGroup === 'one-time' && option1) {
        this.#renderOneTimePlansForOption1(option1);
        this.#updateFormAndButton();
      }

      // Sync with the existing variant picker if present
      const existingPicker = document.querySelector('variant-picker');
      if (existingPicker && option1) {
        const input = existingPicker.querySelector(
          `input[data-fieldset-index="0"][value="${option1}"]`
        );
        if (input instanceof HTMLInputElement && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    } catch (err) {
      console.error('Error in variant tab click:', err);
    }
  }

  #listenForVariantUpdates() {
    const target = this.closest('.shopify-section, dialog, product-card') || document;
    target.addEventListener(
      'variant:update',
      /** @param {Event} event */
      (event) => {
        this.#onVariantUpdate(/** @type {CustomEvent} */ (event));
      },
      { signal: this.#abortController.signal }
    );
  }

  /**
   * @param {CustomEvent} event
   */
  #onVariantUpdate(event) {
    const newVariantId = event.detail?.resource?.id;
    if (!newVariantId) return;

    this.#selectedVariantId = Number(newVariantId);

    // Update variant tabs to match new variant
    this.#syncVariantTabsToVariant(newVariantId);

    this.#updateFormAndButton();
  }

  /**
   * @param {number} variantId
   */
  #syncVariantTabsToVariant(variantId) {
    if (!this.#plansData) return;

    const variantData = this.#plansData.find((v) => v.id === Number(variantId));
    if (!variantData) return;

    // Sync subscribe variant tabs by option1
    const subscribeGroup = this.querySelector('[data-variant-tabs="subscribe"]');
    if (subscribeGroup && variantData.option1) {
      subscribeGroup.querySelectorAll('[data-variant-tab]').forEach(
        /** @param {HTMLElement} tab */
        (tab) => {
          if (tab.dataset.option1 === variantData.option1) {
            tab.classList.add('ssp-variant-tab--active');
          } else {
            tab.classList.remove('ssp-variant-tab--active');
          }
        }
      );
      // Show amount group for this weight and preselect the variant
      this.#showSubscribeAmountGroup(variantData.option1, variantId);
    }

  }

  /**
   * @param {string} option1
   * @param {number} [preselectVariantId]
   */
  #showSubscribeAmountGroup(option1, preselectVariantId) {
    if (!option1) return;

    // Hide all amount groups, show the one matching option1
    this.querySelectorAll('[data-amount-group]').forEach(
      /** @param {HTMLElement} group */
      (group) => {
        const isActive = group.dataset.option1 === option1;
        group.classList.toggle('ssp-amount-group--active', isActive);
      }
    );

    const activeGroup = this.querySelector(`[data-amount-group][data-option1="${option1}"]`);
    if (!activeGroup) return;

    const cards = activeGroup.querySelectorAll('[data-amount-card]');
    if (cards.length === 0) return;

    // Determine which card to select (middle by default)
    let selectedCard = cards[Math.floor(cards.length / 2)];

    if (preselectVariantId) {
      const found = Array.from(cards).find(
        /** @param {HTMLElement} c */
        (c) => Number(c.dataset.variantId) === Number(preselectVariantId)
      );
      if (found) selectedCard = found;
    } else if (this.#selectedVariantId) {
      // Preserve same option2 (amount) when switching weights
      const currentVariant = this.#plansData?.find((v) => v.id === this.#selectedVariantId);
      if (currentVariant?.option2) {
        const sameAmount = Array.from(cards).find(
          /** @param {HTMLElement} c */
          (c) => c.dataset.option2 === currentVariant.option2
        );
        if (sameAmount) selectedCard = sameAmount;
      }
    }

    if (selectedCard) {
      const radio = selectedCard.querySelector('[data-subscribe-variant-input]');
      if (radio instanceof HTMLInputElement) {
        radio.checked = true;
      }
      this.#onAmountCardSelected(/** @type {HTMLElement} */ (selectedCard));
    }
  }

  #initAmountCards() {
    const cards = this.querySelectorAll('[data-amount-card]');
    cards.forEach(
      /** @param {HTMLElement} card */
      (card) => {
        card.addEventListener('click', (e) => {
          try {
            if (e.target instanceof HTMLElement && e.target.tagName === 'INPUT') return;
            const input = card.querySelector('[data-subscribe-variant-input]');
            if (input instanceof HTMLInputElement && !input.checked) {
              input.checked = true;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          } catch (err) {
            console.error('Error in amount card click:', err);
          }
        }, { signal: this.#abortController.signal });

        const input = card.querySelector('[data-subscribe-variant-input]');
        if (input instanceof HTMLInputElement) {
          input.addEventListener('change', () => {
            try {
              this.#onAmountCardSelected(card);
            } catch (err) {
              console.error('Error selecting amount card:', err);
            }
          }, { signal: this.#abortController.signal });
        }
      }
    );
  }

  /**
   * @param {HTMLElement} card
   */
  #onAmountCardSelected(card) {
    const variantId = Number(card.dataset.variantId);
    if (!variantId) return;

    this.#selectedVariantId = variantId;

    // Update card visual selection state
    this.querySelectorAll('[data-amount-card]').forEach(
      /** @param {HTMLElement} c */
      (c) => {
        c.classList.toggle('ssp-plan-card--selected', c === card);
      }
    );

    // Use the selling plan ID directly from the card that was clicked
    const planId = card.dataset.sellingPlanId;
    if (planId) {
      this.#selectedSellingPlanId = String(planId);
      this.#selectedPlansByVariant.set(variantId, this.#selectedSellingPlanId);
    }

    this.#updateFormAndButton();
  }

  /**
   * @param {string} planId
   */
  #updateSellingPlanInput(planId) {
    const section = this.closest('.shopify-section');
    const form = section instanceof HTMLElement ? section.querySelector('form[data-type="add-to-cart-form"]') : null;
    if (!form) {
      // Fallback to any product form in document
      const anyForm = document.querySelector('form[data-type="add-to-cart-form"]');
      if (!(anyForm instanceof HTMLFormElement)) return;
      this.#setOrCreateInput(anyForm, planId);
      return;
    }
    this.#setOrCreateInput(/** @type {HTMLFormElement} */ (form), planId);
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} planId
   */
  #setOrCreateInput(form, planId) {
    const existingInput = form.querySelector('input[name="selling_plan"]');
    /** @type {HTMLInputElement} */
    let input;
    if (existingInput instanceof HTMLInputElement) {
      input = existingInput;
    } else {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      form.appendChild(input);
    }
    input.value = planId;
  }

  #initAccordions() {
    const defaultMode = this.dataset.defaultMode === 'one-time' ? 'one-time' : 'subscribe';
    this.#setPurchaseMode(/** @type {'one-time' | 'subscribe'} */ (defaultMode));

    const accordions = this.querySelectorAll('[data-accordion]');
    accordions.forEach((accordion) => {
      const header = accordion.querySelector('[data-accordion-header]');
      if (!(header instanceof HTMLElement)) return;
      header.addEventListener(
        'click',
        () => {
          const mode = accordion instanceof HTMLElement ? accordion.dataset.accordion : null;
          if (mode && mode !== this.#purchaseMode) {
            this.#setPurchaseMode(/** @type {'one-time' | 'subscribe'} */ (mode));
          }
        },
        { signal: this.#abortController.signal }
      );
    });
  }

  /**
   * @param {'one-time' | 'subscribe'} mode
   */
  #setPurchaseMode(mode) {
    if (mode !== 'one-time' && mode !== 'subscribe') return;
    this.#purchaseMode = mode;

    // Update accordion visual states
    const accordions = this.querySelectorAll('[data-accordion]');
    accordions.forEach((accordion) => {
      const accordionMode = accordion instanceof HTMLElement ? accordion.dataset.accordion : null;
      if (accordionMode === mode && mode) {
        accordion.classList.add('ssp-accordion--selected');
      } else {
        accordion.classList.remove('ssp-accordion--selected');
      }
    });

    this.#updateFormAndButton();
  }

  /**
   * @param {number} variantId
   */
  #updateOneTimePrice(variantId) {
    if (!this.#plansData) return;
    const variant = this.#plansData.find((v) => v.id === Number(variantId));
    if (!variant) return;

    const priceEl = this.querySelector('[data-one-time-price]');
    if (!priceEl) return;

    const priceFormatted = this.#formatMoney(variant.price);
    priceEl.textContent = priceFormatted;
  }

  /**
   * @param {number} variantId
   */
  #renderOneTimePlansForVariant(variantId) {
    if (!this.#plansData) return;
    const variant = this.#plansData.find((v) => v.id === Number(variantId));
    if (!variant) return;
    this.#renderOneTimePlansForOption1(variant.option1 || '');
  }

  

  /**
   * @param {string} option1
   */
  #renderOneTimePlansForOption1(option1) {
    if (!this.#oneTimeProductData || !option1) return;

    const list = this.querySelector('[data-one-time-plans-list]');
    if (!list) return;

    const oneTimeVariants = (this.#oneTimeProductData.variants || []).filter(
      /** @param {{option1: string}} v */
      (v) => v.option1 === option1
    );

    if (oneTimeVariants.length === 0) {
      list.innerHTML = '';
      return;
    }

    let selectedIndex = oneTimeVariants.length > 1 ? 1 : 0;
    if (this.#selectedOneTimeVariantId) {
      const foundIndex = oneTimeVariants.findIndex(
        /** @param {{id: number}} v */
        (v) => v.id === this.#selectedOneTimeVariantId
      );
      if (foundIndex >= 0) selectedIndex = foundIndex;
    }

    const html = oneTimeVariants
      .map(
        /**
         * @param {{id: number, option2: string, price: number, compare_at_price: number, price_per_day: string, discount_percent: string, badge: string, jars_quantity: number}} v
         * @param {number} index
         */
        (v, index) => {
          const isSelected = index === selectedIndex;
          const jars = v.jars_quantity || 1;
          const perJar = Math.round(v.price / jars);
          const perJarCompare = Math.round(v.compare_at_price / jars);

          const priceFormatted = this.#formatMoney(perJar);
          const comparePriceFormatted = perJarCompare > perJar
            ? this.#formatMoney(perJarCompare)
            : '';

          const savingsPercent = perJarCompare > perJar
            ? Math.round(((perJarCompare - perJar) / perJarCompare) * 100)
            : 0;
          const savingsLabel = savingsPercent > 0 ? `Save ${savingsPercent}%` : '';
          const savingsBadge = savingsLabel
            ? `<span class="ssp-plan-card__savings-badge"><span class="ssp-plan-card__savings-badge-label">${this.#escapeHtml(savingsLabel)}</span></span>`
            : '';

          const customBadge = v.badge
            ? `<span class="ssp-plan-card__highlight-badge-text">${this.#escapeHtml(v.badge)}</span>`
            : '';

          // Dynamic per-day price + compare for one-time cards
          const daysFromName = this.#extractDaysFromPlanName(v.option2);
          let perDayHtml = '';
          if (this.#settings?.showPerDayPrice && daysFromName) {
            const interval = { interval: 'day', interval_count: daysFromName };
            const perDayPrice = this.#calculatePerDayPrice(v.price, interval);
            const perDayCompare = v.compare_at_price > v.price
              ? this.#calculatePerDayPrice(v.compare_at_price, interval)
              : '';
            if (perDayPrice) {
              perDayHtml = `<span class="ssp-plan-card__per-day">${perDayPrice}${perDayCompare ? ` <del>${perDayCompare} / day</del>` : ' / day'}</span>`;
            }
          }

          const comparePriceHtml = comparePriceFormatted
            ? `<del class="ssp-plan-card__compare-price">${comparePriceFormatted}</del>`
            : '';

          return `
            <label
              class="ssp-plan-card${isSelected ? ' ssp-plan-card--selected' : ''}"
              data-one-time-plan-card
              data-one-time-variant-id="${v.id}"
              data-one-time-price="${v.price}"
              data-one-time-compare-price="${v.compare_at_price}"
              data-one-time-jars="${jars}"
              data-one-time-per-jar="${perJar}"
            >
              <input
                type="radio"
                name="one-time-plan-${this.dataset.blockId}-${this.dataset.productId}"
                value="${v.id}"
                ${isSelected ? 'checked' : ''}
                data-one-time-plan-input
              >
              <span class="ssp-plan-card__highlight-badge" data-highlight-badge>
                ${customBadge}
              </span>
              <span class="ssp-plan-card__row">
                <span class="ssp-plan-card__info">
                  <span class="ssp-plan-card__name">${this.#escapeHtml(v.option2)}</span>
                  ${perDayHtml}
                </span>
                <span class="ssp-plan-card__badges">
                  ${savingsBadge}
                </span>
                <span class="ssp-plan-card__prices">
                  <span class="ssp-plan-card__price" data-per-jar-price>${priceFormatted}</span>
                  ${comparePriceHtml}
                </span>
              </span>
            </label>
          `;
        }
      )
      .join('');

    list.innerHTML = html;
    this.#initOneTimePlanCards();

    if (oneTimeVariants.length > 0) {
      this.#selectedOneTimeVariantId = oneTimeVariants[selectedIndex].id;
    }
  }

  #initOneTimePlanCards() {
    const cards = this.querySelectorAll('[data-one-time-plan-card]');
    cards.forEach(
      /** @param {HTMLElement} card */
      (card) => {
        card.addEventListener(
          'click',
          /** @param {MouseEvent} e */
          (e) => {
            if (e.target instanceof HTMLElement && e.target.tagName === 'INPUT') return;
            const input = card.querySelector('input[type="radio"]');
            if (input instanceof HTMLInputElement && !input.checked) {
              input.checked = true;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          },
          { signal: this.#abortController.signal }
        );

        const input = card.querySelector('input[type="radio"]');
        if (input instanceof HTMLInputElement) {
          input.addEventListener(
            'change',
            () => {
              this.#onOneTimePlanSelected(card, input);
            },
            { signal: this.#abortController.signal }
          );
        }
      }
    );
  }

  /**
   * @param {HTMLElement} card
   * @param {HTMLInputElement} input
   */
  #onOneTimePlanSelected(card, input) {
    const variantId = Number(input.value);
    this.#selectedOneTimeVariantId = variantId;

    this.querySelectorAll('[data-one-time-plan-card]').forEach((c) => {
      c.classList.remove('ssp-plan-card--selected');
    });
    card.classList.add('ssp-plan-card--selected');

    this.#updateFormAndButton();
  }

  #updateFormAndButton() {
    const section = this.closest('.shopify-section');
    const form = section instanceof HTMLElement ? section.querySelector('form[data-type="add-to-cart-form"]') : null;
    if (!form) return;

    const variantInput = form.querySelector('input[name="id"]');

    if (this.#purchaseMode === 'one-time') {
      // Remove selling plan
      const spInput = form.querySelector('input[name="selling_plan"]');
      if (spInput) spInput.remove();

      // Update variant ID to one-time variant
      if (this.#selectedOneTimeVariantId && variantInput instanceof HTMLInputElement) {
        variantInput.value = String(this.#selectedOneTimeVariantId);
      }
    } else {
      // Update variant ID to subscription variant
      if (this.#selectedVariantId && variantInput instanceof HTMLInputElement) {
        variantInput.value = String(this.#selectedVariantId);
      }
      // Add/restore selling plan
      if (this.#selectedSellingPlanId) {
        this.#updateSellingPlanInput(this.#selectedSellingPlanId);
      }
    }

    this.#updateButtonPrice();
    this.#setupFormSubmitHandler(form);
  }

  /**
   * @param {HTMLFormElement} form
   */
  #setupFormSubmitHandler(form) {
    // Check if we're on index3 template by looking for the redirect attribute
    const isIndex3 = window.location.pathname.includes('hip-joint-chews-special-offer');
    
    // Remove existing listener by cloning
    const newForm = /** @type {HTMLFormElement} */ (form.cloneNode(true));
    form.parentNode?.replaceChild(newForm, form);

    newForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      
      const variantIdInput = newForm.querySelector('input[name="id"]');
      const quantityInput = newForm.querySelector('input[name="quantity"]');
      const sellingPlanInput = newForm.querySelector('input[name="selling_plan"]');

      const variantId = variantIdInput instanceof HTMLInputElement ? variantIdInput.value : null;
      const quantity = quantityInput instanceof HTMLInputElement ? quantityInput.value : '1';
      const sellingPlan = sellingPlanInput instanceof HTMLInputElement ? sellingPlanInput.value : null;

      if (!variantId) return;

      const payload = {
        items: [
          {
            id: variantId,
            quantity: parseInt(quantity, 10),
          },
        ],
      };

      // Add selling_plan if present
      if (sellingPlan) {
        payload.items[0].selling_plan = sellingPlan;
      }

      try {
        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (data.status) {
          // Error occurred
          this.#showErrorToast(data.message || 'Failed to add to cart');
          return;
        }

        // Success - show toast
        this.#showSuccessToast('Item added to cart');

        // Dispatch CartAddEvent to refresh cart and open drawer
        const { CartAddEvent } = await import('@theme/events');
        document.dispatchEvent(
          new CartAddEvent(data, this.id, {
            source: 'subscription-selling-plans',
            itemCount: parseInt(quantity, 10),
          })
        );

        // Redirect to checkout if on index3 template
        if (isIndex3) {
          setTimeout(() => {
            window.location.href = '/checkout';
          }, 500);
        } else {
          // Explicitly open cart drawer for other templates
          setTimeout(() => {
            const cartDrawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
            if (cartDrawer && typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            }
          }, 100);
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
        this.#showErrorToast('An error occurred. Please try again.');
      }
    });
  }

  /**
   * @param {string} message
   */
  #showSuccessToast(message) {
    const toast = document.createElement('div');
    toast.className = 'theme-toast theme-toast--success';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = `
      <span class="theme-toast__icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
        </svg>
      </span>
      <span class="theme-toast__message">${this.#escapeHtml(message)}</span>
    `;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.classList.add('theme-toast--visible');
    }, 10);

    setTimeout(() => {
      toast.classList.remove('theme-toast--visible');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  /**
   * @param {string} message
   */
  #showErrorToast(message) {
    const toast = document.createElement('div');
    toast.className = 'theme-toast theme-toast--error';
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'assertive');
    toast.innerHTML = `
      <span class="theme-toast__icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
        </svg>
      </span>
      <span class="theme-toast__message">${this.#escapeHtml(message)}</span>
    `;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.classList.add('theme-toast--visible');
    }, 10);

    setTimeout(() => {
      toast.classList.remove('theme-toast--visible');
      setTimeout(() => toast.remove(), 300);
    }, 4000);
  }

  #updateButtonPrice() {
    const box = document.getElementById('custom-total-price');
    if (!box) return;

    let price = 0;
    let compare = 0;
    let qty = 1;

    if (this.#purchaseMode === 'one-time') {
      const variant = this.#oneTimeProductData?.variants?.find(
        /** @param {{id: number}} v */
        (v) => v.id === this.#selectedOneTimeVariantId
      );
      if (variant) {
        price = variant.price;
        compare = variant.compare_at_price || variant.price;
        qty = variant.jars_quantity || 1;
      }
    } else {
      const variant = this.#plansData?.find((v) => v.id === this.#selectedVariantId);
      if (variant) {
        const plan = variant.selling_plans?.find(
          /** @param {{id: string}} p */
          (p) => String(p.id) === String(this.#selectedSellingPlanId)
        );
        if (plan) {
          price = plan.price;
          compare = variant.price || plan.compare_at_price || plan.price;
          qty = plan.jars_quantity || 1;
        } else {
          price = variant.price;
          compare = variant.compare_at_price || variant.price;
        }
      }
    }

    const totalPrice = price;
    const totalCompare = compare;

    const compareHTML = totalCompare > totalPrice
      ? `<span style="opacity:0.6; text-decoration:line-through;">$${(totalCompare / 100).toFixed(2)}</span>`
      : '';

    box.innerHTML = `
      <span><b>$${(totalPrice / 100).toFixed(2)}</b></span>
      ${compareHTML}
    `;
  }

  #removeSellingPlanInput() {
    const section = this.closest('.shopify-section');
    const form = section instanceof HTMLElement ? section.querySelector('form[data-type="add-to-cart-form"]') : null;
    if (form) {
      const input = form.querySelector('input[name="selling_plan"]');
      if (input) input.remove();
      return;
    }
    const anyForm = document.querySelector('form[data-type="add-to-cart-form"]');
    if (anyForm) {
      const input = anyForm.querySelector('input[name="selling_plan"]');
      if (input) input.remove();
    }
  }

  #setInitialPurchaseMode() {
    const defaultMode = this.dataset.defaultMode === 'one-time' ? 'one-time' : 'subscribe';
    this.#setPurchaseMode(defaultMode);
  }

  /**
   * @param {number} priceInCents
   * @param {{interval: string, interval_count: number}} intervalInfo
   */
  #calculatePerDayPrice(priceInCents, intervalInfo) {
    const daysPerMonth = Number(this.#settings?.daysPerMonth || 30);
    const interval = intervalInfo.interval || 'month';
    const count = intervalInfo.interval_count || 1;

    let totalDays = 0;
    switch (interval) {
      case 'day':
        totalDays = count;
        break;
      case 'week':
        totalDays = count * 7;
        break;
      case 'month':
        totalDays = count * daysPerMonth;
        break;
      case 'year':
        totalDays = count * daysPerMonth * 12;
        break;
      default:
        totalDays = count * daysPerMonth;
    }

    if (!totalDays) return '';
    const perDay = Math.round(priceInCents / totalDays);
    return this.#formatMoney(perDay);
  }

  /**
   * @param {string} name
   * @returns {number | null}
   */
  #extractDaysFromPlanName(name) {
    if (!name) return null;
    const planName = String(name);
    const daysPerMonth = Number(this.#settings?.daysPerMonth) || 30;

    // "1 Jar (2 Months Supply)" or "(3 Month Supply)"
    const monthsMatch = planName.match(/\((\d+)\s*Months?\s*(?:Supply)?\)/i);
    if (monthsMatch && monthsMatch[1]) return parseInt(monthsMatch[1], 10) * daysPerMonth;

    // "2 Months Supply" without parentheses
    const monthsMatch2 = planName.match(/(\d+)\s*Months?/i);
    if (monthsMatch2 && monthsMatch2[1]) return parseInt(monthsMatch2[1], 10) * daysPerMonth;

    // "(20 Days Supply)" or "(40 Day Supply)"
    const daysMatch = planName.match(/\((\d+)\s*Days?\s*(?:Supply)?\)/i);
    if (daysMatch && daysMatch[1]) return parseInt(daysMatch[1], 10);

    // "20 Days" without parentheses
    const daysMatch2 = planName.match(/(\d+)\s*Days?/i);
    if (daysMatch2 && daysMatch2[1]) return parseInt(daysMatch2[1], 10);

    return null;
  }

  /**
   * @param {number} cents
   */
  #formatMoney(cents) {
    const moneyFormat = this.#settings?.moneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    return moneyFormat.replace('{{amount}}', value).replace('{{ amount }}', value);
  }

  /**
   * @param {string} str
   */
  #escapeHtml(str) {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

if (!customElements.get('subscription-selling-plans')) {
  customElements.define('subscription-selling-plans', SubscriptionSellingPlans);
}
